import React, { Component } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPen, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { ProgressBar, progressOf } from './progress';
import { shortId, dropPositionAt } from './order';

// How long a "confirm" action (e.g. permanent delete) stays armed after the first click.
const CONFIRM_WINDOW_MS = 3000;

/**
 * One task in a list. Handles its own expand/collapse and inline rename, and
 * renders the detail panel lazily via `renderPanel` so the panel only fetches
 * subtasks once it is opened.
 *
 * actions: [{ key, icon, label, onClick(task), tone?, confirm? }]
 * sortable: optional { onDragStart(task), onDragOver(task, position), onDrop(), onDragEnd(),
 *           dropPosition: 'before' | 'after' | 'into' | null, dragging: bool }
 */
class TaskRow extends Component {
  constructor(props) {
    super(props);
    this.state = {
      expanded: false,
      editing: false,
      draft: '',
      armed: null,
      copied: false,
    };
  }

  componentWillUnmount() {
    clearTimeout(this.armTimer);
    clearTimeout(this.copyTimer);
  }

  copyId = () => {
    const id = this.props.task.id;
    const done = () => {
      clearTimeout(this.copyTimer);
      this.setState({ copied: true });
      this.copyTimer = setTimeout(() => this.setState({ copied: false }), 1500);
    };
    if (navigator.clipboard) {
      navigator.clipboard.writeText(id).then(done, () => {});
    }
  }

  // Drag starts from the grip only, so text selection and renaming keep working.
  handleDragStart = (event) => {
    event.stopPropagation();
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', this.props.task.id);
    if (this.rowEl && event.dataTransfer.setDragImage) {
      event.dataTransfer.setDragImage(this.rowEl, 20, 20);
    }
    this.props.sortable.onDragStart(this.props.task);
  }

  handleDragOver = (event) => {
    const { sortable } = this.props;
    if (!sortable || !sortable.accepts()) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';
    sortable.onDragOver(this.props.task, dropPositionAt(this.rowEl.getBoundingClientRect(), event.clientY));
  }

  handleDrop = (event) => {
    const { sortable } = this.props;
    if (!sortable || !sortable.accepts()) return;
    event.preventDefault();
    event.stopPropagation();
    sortable.onDrop();
  }

  toggleExpanded = () => {
    this.setState({ expanded: !this.state.expanded });
  }

  // Tapping anywhere on the row expands it, except on its own controls.
  handleRowClick = (event) => {
    if (this.state.editing) return;
    if (event.target.closest('button, input, a, .task__grip')) return;
    this.toggleExpanded();
  }

  handleRowKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleExpanded();
    }
  }

  startEditing = () => {
    this.committed = false;
    this.setState({ editing: true, draft: this.props.task.name });
  }

  cancelEditing = () => {
    this.setState({ editing: false, draft: '' });
  }

  saveName = () => {
    // Enter and the blur that follows can both call this before state updates.
    if (!this.state.editing || this.committed) return;
    this.committed = true;
    const name = this.state.draft.trim();
    this.setState({ editing: false, draft: '' });
    if (name && name !== this.props.task.name) {
      this.props.onRename(this.props.task, name);
    }
  }

  handleKeyDown = (event) => {
    if (event.key === 'Enter') this.saveName();
    if (event.key === 'Escape') this.cancelEditing();
  }

  runAction = (action) => {
    if (action.confirm && this.state.armed !== action.key) {
      clearTimeout(this.armTimer);
      this.setState({ armed: action.key });
      this.armTimer = setTimeout(() => this.setState({ armed: null }), CONFIRM_WINDOW_MS);
      return;
    }
    clearTimeout(this.armTimer);
    this.setState({ armed: null });
    action.onClick(this.props.task);
  }

  renderName() {
    const { task, allowEdit } = this.props;
    const canRename = allowEdit && task.state !== 'completed' && this.props.onRename;

    if (this.state.editing) {
      return (
        <div className="rename">
          <input
            className="rename__input"
            autoFocus
            value={this.state.draft}
            onChange={(e) => this.setState({ draft: e.target.value })}
            onKeyDown={this.handleKeyDown}
            onBlur={this.saveName}
            aria-label="Task name"
          />
          <button
            className="icon-btn icon-btn--accent"
            onMouseDown={(e) => e.preventDefault()}
            onClick={this.saveName}
            title="Save name"
            aria-label="Save name"
          >
            <FontAwesomeIcon icon={faCheck} />
          </button>
        </div>
      );
    }

    if (canRename) {
      return (
        <span className="task__title">
          <span className="task__name">{task.name}</span>
          <button className="task__rename" onClick={this.startEditing} title="Rename" aria-label={'Rename ' + task.name}>
            <FontAwesomeIcon icon={faPen} />
          </button>
        </span>
      );
    }

    return <span className="task__name">{task.name}</span>;
  }

  render() {
    const { task, actions = [], compact, renderPanel, sortable } = this.props;
    const { expanded, armed, copied } = this.state;
    const { done, estimate } = progressOf(task);
    const isDone = task.state === 'completed';
    const hasDeadline = task.deadline && task.deadline !== 'none';

    return (
      <div
        className={
          'task' +
          (compact ? ' task--compact' : '') +
          (expanded ? ' is-open' : '') +
          (isDone ? ' is-done' : '') +
          (sortable && sortable.dragging ? ' is-dragging' : '') +
          (sortable && sortable.dropPosition ? ' drop-' + sortable.dropPosition : '')
        }
        style={{ '--task-color': task.color }}
      >
        <div
          className={'task__row' + (sortable ? ' task__row--sortable' : '')}
          ref={(el) => { this.rowEl = el; }}
          onDragOver={sortable ? this.handleDragOver : undefined}
          onDrop={sortable ? this.handleDrop : undefined}
          onClick={this.handleRowClick}
          onKeyDown={this.handleRowKeyDown}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-label={(expanded ? 'Hide' : 'Show') + ' details for ' + task.name}
        >
          {sortable && (
            <span
              className="task__grip"
              draggable
              onDragStart={this.handleDragStart}
              onDragEnd={(e) => { e.stopPropagation(); sortable.onDragEnd(); }}
              title="Drag to reorder, or onto a task to make it a subtask"
              aria-hidden="true"
            >
              <FontAwesomeIcon icon={faGripVertical} />
            </span>
          )}
          <span className="task__dot" aria-hidden="true" />

          <div className="task__main">
            {this.renderName()}
            <span className="task__meta">
              <button
                className={'task__id' + (copied ? ' is-copied' : '')}
                onClick={this.copyId}
                title={'Task ID ' + task.id + ' (click to copy full ID)'}
                aria-label={'Copy task ID ' + task.id}
              >
                {copied ? 'copied' : '#' + shortId(task.id)}
              </button>
              {hasDeadline && <span className="task__deadline">Due {task.deadline}</span>}
            </span>
          </div>

          <div className="task__units" title={`${done} of ${estimate} units done`}>
            {done}
            <span>/{estimate}</span>
          </div>

          <div className="task__progress">
            <ProgressBar task={task} size={compact ? 'sm' : 'md'} />
          </div>

          <div className="task__actions">
            {actions.map((action) => {
              const isArmed = armed === action.key;
              return (
                <button
                  key={action.key}
                  className={
                    'icon-btn' +
                    (action.tone ? ' icon-btn--' + action.tone : '') +
                    (isArmed ? ' is-armed' : '')
                  }
                  onClick={() => this.runAction(action)}
                  title={isArmed ? 'Click again to confirm' : action.label}
                  aria-label={isArmed ? 'Confirm: ' + action.label : action.label}
                >
                  <FontAwesomeIcon icon={action.icon} />
                  {isArmed && <span className="icon-btn__confirm">Sure?</span>}
                </button>
              );
            })}
          </div>
        </div>

        {expanded && renderPanel && <div className="task__panel">{renderPanel(task)}</div>}
      </div>
    );
  }
}

export default TaskRow;
